/* eslint-disable */
import {
  CheckCircleFilled,
  ImportOutlined, InboxOutlined, WarningOutlined
} from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space, Spin, Typography,
  UploadProps
} from 'antd';
import { ColumnProps } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { getAccessToken } from 'common/services/common/storage';
import {
  exportAttendanceRecordsService
} from 'common/services/extends/attendanceRecords';
import { ROUTE_PATHS } from 'common/utils/constant';
import roles, { getPermission } from 'configs/roles';

type ImportTable = {
  key: number,
  nameCol: string,
  CCCDCol: string,
  status: 'invalid' | 'valid',
  statusWarning: 'invalid-warning' | 'valid',
  errorStatusCol?: Array<string>,
  warningStatusCol?: Array<string>,
  position?: string,
  note?: string,
};

const AttendanceRecordsImport: React.FC<ActiveRoles> = ({ roleOther }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const payrollPeriodId = Number(searchParams.get('payrollPeriodId'));
  const staffFullTimeId = Number(searchParams.get('staffFullTimeId'));
  const projectId = Number(searchParams.get('projectId'));
  const { t } = useTranslation();
  const [fileList, setFileList] = React.useState<RcFile | null>(null);

  const [jsonFile, setJsonFile] = React.useState<string>('');
  const [isLoadingJsonFile, setIsLoadingJsonFile] = React.useState<boolean>(false);
  const loadedJSonFileRef = React.useRef<boolean>(false);

  const { mutate: downloadAttendanceRecords, isLoading: downloadLoading } = useMutation(
    ['downloadAttendanceRecords'],
    () => exportAttendanceRecordsService({
      _payrollPeriodId: payrollPeriodId
    }),
    {
      onSuccess: (data) => {
        if (data?.link) {
          window.open(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const streamFile = async (fileRes: RcFile, preview: boolean) => {
    try {
      setIsLoadingJsonFile(true);
      const bodyForm = new FormData();
      bodyForm.append('file', fileRes);
      bodyForm.append('preview', preview ? '1' : '0');
      const token = getAccessToken();
      // request with form data to api for endpoint /api/v1/staffs/import
      const res = await fetch(`/api/v1/attendance-records/import?_payrollPeriodId=${payrollPeriodId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: bodyForm,
      });

      if (!res.ok) {
        console.log('Fail with status: ', res?.status);
        if (res.status === 401) {
          navigate('/login');
        } else {
          message.error(t('message.uploadError'));
        }
        setIsLoadingJsonFile(false);
        return;
      }
      const reader = res.body?.getReader();

      let result;
      const decoder = new TextDecoder();
      if (reader) {
        do {
          // eslint-disable-next-line no-await-in-loop
          result = await reader.read();
          const record = decoder.decode(result.value, { stream: true });
          if (preview) {
            setJsonFile((prev) => prev + record);
          }
        } while (!result.done);

        if (result.done) {
          loadedJSonFileRef.current = true;
          setIsLoadingJsonFile(false);
          if (!preview) {
            message.success(t('message.updateSuccess'));
            setFileList(null);
            setJsonFile('');
            navigate(`${ROUTE_PATHS.TIMEKEEPING_DETAIL}?payrollPeriodId=${payrollPeriodId}&staffFullTimeId=${staffFullTimeId}&projectId=${projectId}`);
          }
        }
      }
    } catch (e) {
      setIsLoadingJsonFile(false);
      // Emtry
    }
  };

  const handleBeforeUpload = async (fileRes: RcFile): Promise<boolean> => {
    try {
      if (payrollPeriodId) {
        setFileList(fileRes);
        streamFile(fileRes, true);
      }
    } catch {
      message.error('Import failed');
    }
    return false;
  };
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    disabled: isLoadingJsonFile,
  };

  const onCancel = () => {
    loadedJSonFileRef.current = true;
    setJsonFile('');
    setFileList(null);
  };

  const columns: ColumnProps<ImportTable>[] = [
    {
      title: t('contact.problem'),
      dataIndex: 'statusCol',
      key: 'statusCol',
      width: 380,
      fixed: 'left',
      sorter: {
        compare: (a: ImportTable, b: ImportTable) => a.status.localeCompare(b.status),
      },
      render: (text, record) => {
        if (record.status === 'valid' && record.statusWarning === 'valid') {
          return (
            <Space>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Typography.Text>Hợp lệ</Typography.Text>
            </Space>
          );
        }
        return (
          <ul style={{ paddingLeft: 0 }}>
            {
              record.errorStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f5222d' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
            {
              record.warningStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f69320' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
          </ul>
        );
      }
    },
    {
      title: t('system.name'),
      dataIndex: 'nameCol',
      key: 'nameCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'CCCDCol',
      key: 'CCCDCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('system.position'),
      dataIndex: 'position',
      key: 'position',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('system.note'),
      dataIndex: 'note',
      key: 'note',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
  ];

  const tableData = useMemo(() => {
    if (!loadedJSonFileRef.current) return null;
    if (jsonFile === '') return null;

    const lines = jsonFile.split('\n');
    const rowData: ImportTable[] = [];
    let totalData: number = 0;
    let errorsData: number = 0;
    let warningsData: number = 0;
    lines.forEach((line) => {
      if (line.trim()) {
        const json = JSON.parse(line);
        if (json.status === 'processing') {
          const { row, total, errors, warnings } = json.data;

          if (!row) {
            if (Object.prototype.hasOwnProperty.call(json.data, 'warnings')) {
              warningsData = warnings;
            }
            if (Object.prototype.hasOwnProperty.call(json.data, 'total')) {
              totalData = total;
            }
            if (Object.prototype.hasOwnProperty.call(json.data, 'errors')) {
              errorsData = errors;
            }
            return;
          }

          const rowResult: ImportTable = {
            key: row?.rowNumber,
            nameCol: row?.fullname,
            CCCDCol: row?.idCardNumber,
            status: row?.errors?.length > 0 ? 'invalid' : 'valid',
            statusWarning: row?.warnings && row?.warnings?.length > 0 ? 'invalid-warning' : 'valid',
            errorStatusCol: row?.errors,
            warningStatusCol: row?.warnings,
            position: row?.position,
            note: row?.note,
          };
          rowData.push(rowResult);
        }
      }
    });

    return {
      total: totalData,
      errors: errorsData,
      rows: rowData,
      warnings: warningsData,
    };
  }, [jsonFile, loadedJSonFileRef.current]);

  return (
    <div className="p-employeeImport">
      <HeaderPage
        fixed
        title={t('timesheet.import')}
        rightHeader={(
          tableData && (
            <Space>
              <Button
                type="primary"
                disabled={!tableData.rows.length || tableData.errors > 0
                  || !getPermission(roleOther, roles.ATTENDANCE_RECORD_IMPORT)}
                onClick={() => {
                  Modal.confirm({
                    className: 't-pagetable_deleteLanguageModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('employee.importConfirm'),
                    onOk: () => {
                      if (fileList && payrollPeriodId) {
                        streamFile(fileList, false);
                      }
                    },
                  });
                }}
              >
                {t('system.ok')}
              </Button>
              <Button onClick={onCancel} disabled={isLoadingJsonFile}>
                {t('system.cancel')}
              </Button>
            </Space>
          ))}
      />
      <Spin spinning={isLoadingJsonFile}>
        <div className="t-mainlayout_wrapper">
          <Card
            className="p-employeeImport_card"
            title={(
              <Space style={{ width: '100%', justifyContent: 'space-between' }} direction="horizontal">
                {t('system.import')}
                {tableData ? null : (
                  <Button
                    type="primary"
                    disabled={downloadLoading
                      || !getPermission(roleOther, roles.ATTENDANCE_RECORD_EXPORT_INDEX)}
                    onClick={() => downloadAttendanceRecords()}
                    loading={downloadLoading}
                  >
                    <Space>
                      <ImportOutlined />
                      {t('system.downloadSampleFile')}
                    </Space>
                  </Button>
                )}
              </Space>
            )}
          >
            {tableData ? (
              <div>
                <div className="p-employeeImport_card_general">
                  <Space className="p-employeeImport_card_general-item" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Tổng số dòng dữ liệu</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <Typography.Text>{tableData.total}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item success" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng hợp lệ</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <Typography.Text>{tableData.total - tableData.errors}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item error" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng lỗi</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f5222d' }} />
                      <Typography.Text>
                        {tableData.errors}
                      </Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item warning" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng cảnh báo</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f69320' }} />
                      <Typography.Text>
                        {tableData.warnings}
                      </Typography.Text>
                    </Space>
                  </Space>
                </div>
                <div className="p-employeeImport_card_table">
                  <PageTable
                    noCheckbox
                    isLoading={false}
                    tableProps={{
                      columns,
                      pageData: tableData.rows || [],
                      initShowColumns: ['statusCol', 'nameCol', 'CCCDCol', 'position', 'note'],
                      noBaseCol: true,
                      noDeleteLanguage: true,
                      rowClassName: (record) => (record.status === 'valid' ? 'p-employeeImport_card_table-validRow' : 'p-employeeImport_card_table-invalidRow'),
                      isHidePagination: true
                    }}
                  />
                </div>
              </div>
            ) : (
              <Dragger
                {...uploadProps}
                disabled={isLoadingJsonFile
                  || !getPermission(roleOther, roles.ATTENDANCE_RECORD_IMPORT)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {' '}
                  {t('media.dndText')}
                </p>
              </Dragger>
            )}
          </Card>
        </div>
      </Spin>
    </div>
  );
};

export default AttendanceRecordsImport;
